import { useTranslation } from "react-i18next";

import { Button } from "@wellcheck/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@wellcheck/components/ui/dialog";

import { useReissueLink } from "../api/useReissueLink";

/**
 * LinkReissueDialog — 回答リンク再発行の確認ダイアログ (ADR-0061 Decision 2)。
 *
 * POST の失敗を別文言で出しても、GET で状態を開示済みなので新しいオラクルにはならない
 * (ADR-0061 Decided Questions #10)。
 */
export interface LinkReissueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: string;
  respondentId: string;
  maskedEmail: string;
}

export function LinkReissueDialog({
  open,
  onOpenChange,
  campaignId,
  respondentId,
  maskedEmail,
}: LinkReissueDialogProps) {
  const { t } = useTranslation("respond");
  const mutation = useReissueLink();
  const sendFailed = mutation.isError;
  const completed = mutation.isSuccess;

  const handleConfirm = () =>
    mutation.mutate({
      campaignRecordId: campaignId,
      respondentRecordId: respondentId,
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("linkReissue.dialogTitle", { maskedEmail })}</DialogTitle>
          <DialogDescription>{t("linkReissue.dialogDescription")}</DialogDescription>
        </DialogHeader>

        {completed ? (
          <p className="text-sm text-muted-foreground">{t("linkReissue.completedDescription")}</p>
        ) : (
          <>
            {sendFailed ? (
              <p className="text-sm text-destructive">{t("linkReissue.sendError")}</p>
            ) : null}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={mutation.isPending}
              >
                {t("linkReissue.cancel")}
              </Button>
              <Button onClick={handleConfirm} disabled={mutation.isPending}>
                {mutation.isPending
                  ? t("linkReissue.sending")
                  : sendFailed
                    ? t("linkReissue.retry")
                    : t("linkReissue.confirm")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
